import type { IFetchDataParams, IUserTransaction } from '@/interfaces';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { handleApiResponse } from '@/functions';
import { useInvalidateQueries } from '@/hooks';
import { apiClient } from '@/components/api';
import type { Paginated } from '@/lib/interface';
import routes from '@/lib/routes';
import dayjs from 'dayjs';

type TParams = IFetchDataParams & {
	userId: string;
	config?: {
		staleTime?: number;
	};
};
type TResponseData = Paginated<IUserTransaction>;

export const useRequestGetUserTransactions = (params: TParams, run = true) => {
	const queryClient = useQueryClient();

	const staleTime = params?.config?.staleTime ?? (queryClient.getDefaultOptions().queries?.staleTime as any);

	const { invalidate } = useInvalidateQueries();
	const { isFetching, data, error, dataUpdatedAt } = useQuery({
		queryKey: [routes.api.users.base, 'transactions', params, run],
		queryFn: ({ queryKey: [, , _params, _run] }) =>
			!_run || !(_params as TParams)?.userId ? Promise.resolve(null) : fetchQuery(_params as TParams),
		staleTime
	});

	const userTransactions = data?.data ?? [];

	const fetchQuery = (params: TParams) => {
		const { sortType, sortBy, limit, page } = params;
		const queryParams = new URLSearchParams();
		if (sortType) queryParams.append('sortType', sortType);
		if (sortBy) queryParams.append('sortBy', sortBy);
		if (limit) queryParams.append('limit', limit.toString());
		if (page) queryParams.append('page', page.toString());

		const queryString = queryParams.toString();
		const url = queryString
			? `${routes.api.users.transactions(params.userId)}?${queryString}`
			: routes.api.users.transactions(params.userId);

		return apiClient
			.get<TResponseData>(url)
			.then(handleApiResponse)
			.then(data =>
				data
					? {
							...data,
							data: [...data.data].map(l => ({ ...l, createdAt: dayjs(l.createdAt).toDate() }))
						}
					: data
			);
	};

	const fetchUserTransactions = (params: TParams) => fetchQuery(params);

	const refresh = () => invalidate(routes.api.users.base, 'transactions', params);

	return {
		userTransactionsUpdatedAt: dataUpdatedAt,
		isLoadingUserTransactions: isFetching,
		fetchUserTransactions,
		userTransactions,
		refresh,
		error,
		data
	};
};
